/**
 * Amplitude Analytics Service for Insight Atlas AI (Web)
 * 
 * AARRR Framework: Acquisition, Activation, Engagement, Retention, Monetization
 * Includes Session Replay and Autocapture for deep behavioral analysis.
 */

import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import packageJson from '../../../package.json';
import type {
  AnalyticsUser,
  UserProperties,
  PageViewedProps,
  SessionStartedProps,
  LoginScreenViewedProps,
  LoginStartedProps,
  LoginFailedProps,
  UserSignedUpProps,
  UserLoggedInProps,
  ResearchStartedProps,
  ResearchCompletedProps,
  ResearchViewedProps,
  ResearchFailedProps,
  FullReportReadProps,
  ArticleSavedProps,
  ArticleLikedProps,
  ArticleUnlikedProps,
  ArticleSharedProps,
  ExploreBrowsedProps,
  ExploreCardClickedProps,
  PodcastPlayedProps,
  ResearchPrivacyToggledProps,
  SourceClickedProps,
  LimitReachedProps,
  UpgradeModalViewedProps,
  UpgradeStartedProps,
  UpgradeCompletedProps,
  TabChangedProps,
  ModalOpenedProps,
  ModalClosedProps,
  ProfileViewedProps,
  ErrorOccurredProps,
  APIErrorProps,
  PlanAbandonedProps,
  PushNotificationOpenedProps,
  AnalyticsEventName,
} from './types';

// Configuration - API key with fallback
const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY || 'fc75ff968fb0f603922cc69bbb039bf3';
// Note: The fallback key is intentionally included for easier initial setup
const IS_DEBUG = process.env.REACT_APP_AMPLITUDE_DEBUG === 'true';
const IS_DEV = process.env.NODE_ENV === 'development';
const APP_VERSION = process.env.REACT_APP_VERSION || packageJson.version || '0.6.0';
const WEB_SESSION_COUNT_KEY = 'insight_atlas_web_session_count';
const WEB_LAST_OPEN_KEY = 'insight_atlas_web_last_open';
type AnonymousEventName =
  | 'Landing Page Viewed'
  | 'Login Screen Viewed'
  | 'Auth CTA Clicked'
  | 'Login Started'
  | 'Login Failed'
  // Passive browsing events visible to unauthenticated visitors
  | 'Page Viewed'
  | 'Session Started'
  | 'Explore Card Clicked'
  | 'Research Viewed';
const ANONYMOUS_ALLOWED_EVENTS = new Set<AnonymousEventName>([
  'Landing Page Viewed',
  'Login Screen Viewed',
  'Auth CTA Clicked',
  'Login Started',
  'Login Failed',
  'Page Viewed',
  'Session Started',
  'Explore Card Clicked',
  'Research Viewed',
]);

/**
 * Strip non-serializable values from analytics event properties before passing
 * to amplitude.track(). Amplitude's Timeline.apply() calls JSON.stringify on
 * every event unconditionally inside template-literal log messages, so a
 * circular reference — e.g. an SVGSVGElement with a React fiber back-pointer —
 * throws an unhandled promise rejection even when logging is disabled.
 */
function sanitizeProperties(props: Record<string, unknown>): Record<string, unknown> {
  const safe: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(props)) {
    if (
      value === null ||
      value === undefined ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      safe[key] = value
    } else if (Array.isArray(value)) {
      safe[key] = value.filter(
        (v) => v === null || typeof v !== 'object' || v instanceof Date
      )
    } else {
      try {
        JSON.stringify(value)
        safe[key] = value
      } catch {
        if (IS_DEV) {
          console.warn(`[Amplitude] Dropping non-serializable property: ${key}`)
        }
      }
    }
  }
  return safe
}

class AmplitudeAnalytics {
  private initialized = false;
  private platform: 'web_desktop' | 'web_mobile' = 'web_desktop';
  private sessionStartTime: number = Date.now();
  private enabled = true;
  private userResearchCount = 0;
  private signupDate: Date | null = null;
  private isAuthenticated = false; // Only track events when user is authenticated
  private sessionReplayEnabled = false;
  private hadExistingStoredUserId = false;
  private initialSessionTracked = false;
  private readonly handleResize = () => this.detectPlatform();

  /**
   * Initialize Amplitude with manual tracking.
   *
   * Autocapture stays disabled so we control which anonymous events are allowed.
   * Session Replay can still start at init time for deployed pre-auth browsing.
   */
  init(): void {
    if (this.initialized) return;

    // Check for user opt-out
    this.enabled = localStorage.getItem('analytics_optout') !== 'true';
    
    if (!this.enabled) {
      console.log('[Amplitude] User has opted out of analytics');
      return;
    }

    if (!AMPLITUDE_API_KEY) {
      console.warn('[Amplitude] No API key found');
      return;
    }

    try {
      // Check for existing user ID (email) from previous login
      const existingUserId = localStorage.getItem('amplitude_user_id');
      this.hadExistingStoredUserId = Boolean(existingUserId);
      
      console.log('[Amplitude] ===== INITIALIZING =====');
      console.log('[Amplitude] Existing user (from localStorage):', existingUserId || 'none (anonymous)');
      
      // Initialize Amplitude WITHOUT autocapture to prevent anonymous events
      // We'll manually track events AFTER we know the user's identity
      amplitude.init(AMPLITUDE_API_KEY, existingUserId || undefined, {
        // DISABLE autocapture - it sends events before user is identified
        autocapture: false,
        defaultTracking: false,
      });

      // If we had an existing user ID (email), verify it's set.
      // Most tracking still waits for explicit identify() from auth recovery.
      if (existingUserId) {
        const currentUserId = amplitude.getUserId();
        console.log('[Amplitude] After init - getUserId():', currentUserId);
        if (currentUserId !== existingUserId) {
          // Force set the user ID
          amplitude.setUserId(existingUserId);
          console.log('[Amplitude] Re-set userId to:', amplitude.getUserId());
        }
      }

      // Detect platform
      this.detectPlatform();
      window.addEventListener('resize', this.handleResize);

      this.initialized = true;
      this.enableSessionReplay();
      console.log('[Amplitude] Initialized', existingUserId ? `for user ${existingUserId}` : '(waiting for login)');

      // DON'T track session start here automatically; authenticated session
      // tracking still waits for the auth check, while the anonymous allowlist
      // can emit its own pre-auth events as needed.

    } catch (error) {
      console.error('[Amplitude] Failed to initialize:', error);
    }
  }

  private enableSessionReplay(): void {
    if (this.sessionReplayEnabled) return;

    // ONLY in production - Session Replay causes CORS errors in localhost development
    if (IS_DEV) {
      console.log('[Amplitude] Session Replay disabled (development mode)');
      return;
    }

    try {
      const sessionReplay = sessionReplayPlugin({
        sampleRate: 1, // 100% of sessions - adjust for high volume
      });
      amplitude.add(sessionReplay);
      this.sessionReplayEnabled = true;
      console.log('[Amplitude] Session Replay enabled (production)');
    } catch (error) {
      console.error('[Amplitude] Failed to enable Session Replay:', error);
    }
  }

  private ensureInitialized(): boolean {
    if (!this.initialized) {
      this.init();
    }
    return this.initialized;
  }

  private getDeviceClass(): 'desktop' | 'mobile' | 'tablet' {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  private getViewportMetrics(): Record<string, unknown> {
    return {
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      device_class: this.getDeviceClass(),
    };
  }

  private getAttributionProperties(): Record<string, unknown> {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    const utmContent = params.get('utm_content');
    const utmTerm = params.get('utm_term');

    return {
      referrer: document.referrer || 'direct',
      ...(utmSource ? { utm_source: utmSource } : {}),
      ...(utmMedium ? { utm_medium: utmMedium } : {}),
      ...(utmCampaign ? { utm_campaign: utmCampaign } : {}),
      ...(utmContent ? { utm_content: utmContent } : {}),
      ...(utmTerm ? { utm_term: utmTerm } : {}),
    };
  }

  private consumeWebOpenMetrics(): { daysSinceLastOpen: number; sessionCount: number } {
    try {
      const lastOpenRaw = localStorage.getItem(WEB_LAST_OPEN_KEY);
      const sessionCountRaw = localStorage.getItem(WEB_SESSION_COUNT_KEY);
      const previousSessionCount = Number.parseInt(sessionCountRaw || '0', 10);
      const sessionCount = Number.isFinite(previousSessionCount) && previousSessionCount > 0
        ? previousSessionCount + 1
        : 1;

      const now = Date.now();
      const lastOpenMs = lastOpenRaw ? new Date(lastOpenRaw).getTime() : Number.NaN;
      const daysSinceLastOpen = Number.isFinite(lastOpenMs)
        ? Math.max(0, Math.floor((now - lastOpenMs) / (1000 * 60 * 60 * 24)))
        : 0;

      localStorage.setItem(WEB_LAST_OPEN_KEY, new Date(now).toISOString());
      localStorage.setItem(WEB_SESSION_COUNT_KEY, String(sessionCount));

      return { daysSinceLastOpen, sessionCount };
    } catch (error) {
      console.warn('[Amplitude] Failed to update web open metrics:', error);
      return { daysSinceLastOpen: 0, sessionCount: 1 };
    }
  }

  private trackEvent(
    eventName: AnalyticsEventName | string,
    properties?: Record<string, unknown>,
    options?: { allowAnonymous?: boolean }
  ): void {
    if (!this.enabled) {
      if (IS_DEBUG || IS_DEV) {
        console.log(`[Amplitude Mock] ${eventName}`, properties);
      }
      return;
    }

    if (!this.ensureInitialized()) {
      if (IS_DEBUG || IS_DEV) {
        console.log(`[Amplitude] Skipping (not initialized): ${eventName}`, properties);
      }
      return;
    }

    if (!this.isAuthenticated && !options?.allowAnonymous) {
      if (IS_DEBUG || IS_DEV) {
        console.log(`[Amplitude] Skipping (not authenticated): ${eventName}`, properties);
      }
      return;
    }

    try {
      amplitude.track(eventName, sanitizeProperties({
        ...properties,
        platform: this.platform,
        app_version: APP_VERSION,
        ...this.getViewportMetrics(),
        timestamp: new Date().toISOString(),
      }));

      if (IS_DEBUG) {
        console.log(`[Amplitude] Tracked: ${eventName}`, properties);
      }
    } catch (error) {
      console.error('[Amplitude] Failed to track:', error);
    }
  }
  
  /**
   * Called after auth is determined to track initial session
   * This ensures the session start event has the correct user ID
   */
  trackInitialSession(): void {
    if (!this.enabled || !this.ensureInitialized() || !this.isAuthenticated || this.initialSessionTracked) return;

    this.initialSessionTracked = true;
    const landingPage = window.location.pathname + window.location.hash;
    const { daysSinceLastOpen, sessionCount } = this.consumeWebOpenMetrics();
    
    this.trackSessionStarted({
      ...this.getAttributionProperties(),
      landing_page: landingPage,
      device_type: this.getDeviceClass(),
      is_returning_user: this.hadExistingStoredUserId,
      days_since_last_visit: daysSinceLastOpen,
    });
    
    // Track page view manually
    this.trackPageViewed({
      page: landingPage,
      ...this.getAttributionProperties(),
    });

    this.trackAppOpened(daysSinceLastOpen, sessionCount);
  }

  /**
   * Detect if user is on mobile or desktop
   */
  private detectPlatform(): void {
    this.platform = window.innerWidth < 768 ? 'web_mobile' : 'web_desktop';
  }

  /**
   * Identify user after login
   * This sets both the user ID and user properties in Amplitude
   * 
   * IMPORTANT: We use EMAIL as the user ID so users are clearly identifiable in Amplitude
   * After this is called, events will be tracked. Before this, no events are tracked.
   */
  async identify(user: AnalyticsUser): Promise<void> {
    if (!this.enabled) {
      console.log('[Amplitude] Skipping identify - analytics disabled');
      return;
    }

    try {
      if (!this.initialized) {
        this.init();
      }

      if (!this.initialized) {
        console.warn('[Amplitude] Failed to initialize before identify');
        return;
      }

      // Use EMAIL as the user ID - this makes users easily identifiable in Amplitude dashboard
      const userId = user.email;
      
      console.log('[Amplitude] ===== IDENTIFYING USER =====');
      console.log('[Amplitude] User ID (email):', userId);
      console.log('[Amplitude] User name:', user.name);
      console.log('[Amplitude] Internal ID:', user.id);
      
      // CRITICAL: Set user ID first - this associates all future events with this user
      amplitude.setUserId(userId);
      
      // Store in localStorage for session persistence and cross-tab identification
      localStorage.setItem('amplitude_user_id', userId);

      // Verify the user ID was set immediately
      const verifiedUserId = amplitude.getUserId();
      console.log('[Amplitude] After setUserId - getUserId() returns:', verifiedUserId);
      
      if (verifiedUserId !== userId) {
        console.error('[Amplitude] CRITICAL: setUserId failed! Expected:', userId, 'Got:', verifiedUserId);
        // Try setting it again
        amplitude.setUserId(userId);
        console.log('[Amplitude] Retry setUserId - now getUserId() returns:', amplitude.getUserId());
      }

      // Calculate days since signup
      const signupDate = user.created_at ? new Date(user.created_at) : new Date();
      this.signupDate = signupDate;
      const daysSinceSignup = Math.floor((Date.now() - signupDate.getTime()) / (1000 * 60 * 60 * 24));

      // Set user properties using Identify API
      const identifyObj = new amplitude.Identify()
        .set('email', user.email)
        .set('name', user.name)
        .set('internal_user_id', String(user.id)) // Keep internal ID as a property
        .set('plan', user.plan || 'free')
        .set('platform', this.platform)
        .set('app_version', APP_VERSION)
        .set('device_class', this.getDeviceClass())
        .set('viewport_width', window.innerWidth)
        .set('viewport_height', window.innerHeight)
        .set('days_since_signup', daysSinceSignup)
        .setOnce('signup_date', user.created_at || new Date().toISOString())
        .setOnce('first_seen', new Date().toISOString());

      if (user.avatar_url) {
        identifyObj.set('avatar_url', user.avatar_url);
      }

      const attributionProps = this.getAttributionProperties();
      const utmSource = typeof attributionProps.utm_source === 'string' ? attributionProps.utm_source : undefined;
      const utmMedium = typeof attributionProps.utm_medium === 'string' ? attributionProps.utm_medium : undefined;
      const utmCampaign =
        typeof attributionProps.utm_campaign === 'string' ? attributionProps.utm_campaign : undefined;

      if (utmSource) {
        identifyObj
          .setOnce('acquisition_source', utmSource)
          .setOnce('acquisition_medium', utmMedium || 'unknown')
          .setOnce('acquisition_campaign', utmCampaign || 'unknown');
      }

      // Send the identify call and wait for it to complete
      await amplitude.identify(identifyObj).promise;
      
      // Flush to ensure the identify is sent immediately
      await amplitude.flush().promise;
      
      // NOW we can track events - user is authenticated
      this.isAuthenticated = true;
      this.initialSessionTracked = false;
      this.enableSessionReplay();
      
      console.log('[Amplitude] User identified successfully:', userId, '| Final getUserId():', amplitude.getUserId());

    } catch (error) {
      console.error('[Amplitude] Failed to identify user:', error);
    }
  }

  /**
   * Reset analytics on logout
   * Note: Call analytics.track('User Logged Out', ...) BEFORE calling reset()
   * to ensure the logout event is attributed to the correct user
   * 
   * After this is called, NO events will be tracked until identify() is called again.
   * This prevents anonymous events from being sent when user is logged out.
   */
  reset(): void {
    if (!this.enabled) return;

    try {
      // STOP tracking events - user is no longer authenticated
      this.isAuthenticated = false;
      
      // Clear user identity from Amplitude
      amplitude.reset();
      
      // Clear stored user ID so next session starts anonymous
      localStorage.removeItem('amplitude_user_id');
      
      // Reset internal state
      this.sessionStartTime = Date.now();
      this.userResearchCount = 0;
      this.signupDate = null;
      this.hadExistingStoredUserId = false;
      this.initialSessionTracked = false;
      
      console.log('[Amplitude] User identity reset - tracking disabled until next login');
    } catch (error) {
      console.error('[Amplitude] Failed to reset:', error);
    }
  }

  /**
   * Track a custom event
   * 
   * IMPORTANT: Events are ONLY tracked when user is authenticated.
   * This prevents anonymous events since all users must be logged in.
   */
  track(eventName: AnalyticsEventName | string, properties?: Record<string, unknown>): void {
    this.trackEvent(eventName, properties);
  }

  trackAnonymous(eventName: AnonymousEventName, properties?: Record<string, unknown>): void {
    if (!ANONYMOUS_ALLOWED_EVENTS.has(eventName)) {
      if (IS_DEBUG || IS_DEV) {
        console.warn(`[Amplitude] Skipping unapproved anonymous event: ${eventName}`);
      }
      return;
    }

    this.trackEvent(eventName, properties, { allowAnonymous: true });
  }

  /** Track Page Viewed — allowed for unauthenticated users to capture pre-login browsing */
  trackPageViewedAnonymous(props: PageViewedProps): void {
    this.trackAnonymous('Page Viewed', props);
  }

  /**
   * Increment a user property
   */
  incrementUserProperty(property: string, by: number = 1): void {
    if (!this.enabled || !this.isAuthenticated || !this.initialized) return;
    try {
      const identify = new amplitude.Identify().add(property, by);
      amplitude.identify(identify);
    } catch (error) {
      console.error('[Amplitude] Failed to increment:', error);
    }
  }

  /**
   * Set a user property
   */
  setUserProperty(property: string, value: unknown): void {
    if (!this.enabled || !this.isAuthenticated || !this.initialized) return;
    try {
      const identify = new amplitude.Identify().set(property, value as string | number | boolean | string[] | number[]);
      amplitude.identify(identify);
    } catch (error) {
      console.error('[Amplitude] Failed to set property:', error);
    }
  }

  /**
   * Set user properties in bulk
   */
  setUserProperties(properties: UserProperties): void {
    if (!this.enabled || !this.isAuthenticated || !this.initialized) return;
    try {
      const identify = new amplitude.Identify();
      Object.entries(properties).forEach(([key, value]) => {
        if (value !== undefined) {
          identify.set(key, value as string | number | boolean | string[] | number[]);
        }
      });
      amplitude.identify(identify);
    } catch (error) {
      console.error('[Amplitude] Failed to set properties:', error);
    }
  }

  /**
   * Opt out of analytics
   */
  optOut(): void {
    localStorage.setItem('analytics_optout', 'true');
    this.enabled = false;
    amplitude.setOptOut(true);
    console.log('[Amplitude] User opted out');
  }

  /**
   * Opt back in to analytics
   */
  optIn(): void {
    localStorage.removeItem('analytics_optout');
    this.enabled = true;
    amplitude.setOptOut(false);
    if (!this.initialized) {
      this.init();
    }
    console.log('[Amplitude] User opted in');
  }

  /**
   * Check if user is authenticated (for conditional tracking)
   */
  isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Track logout event - must be called BEFORE reset() to attribute to correct user
   * This method bypasses the isAuthenticated check since we're about to log out
   */
  trackLogout(sessionDurationMinutes?: number): void {
    if (!this.enabled) return;
    
    const duration = sessionDurationMinutes ?? Math.floor((Date.now() - this.sessionStartTime) / 1000 / 60);
    
    try {
      // Track directly without the isAuthenticated check (we're logging out)
      amplitude.track('User Logged Out', {
        session_duration_minutes: duration,
        method: 'manual',
        platform: this.platform,
        timestamp: new Date().toISOString(),
      });
      
      console.log('[Amplitude] Tracked: User Logged Out');
    } catch (error) {
      console.error('[Amplitude] Failed to track logout:', error);
    }
  }

  // ============================================================================
  // ACQUISITION Events
  // ============================================================================

  trackPageViewed(props: PageViewedProps): void {
    this.track('Page Viewed', props);
  }

  trackSessionStarted(props: SessionStartedProps): void {
    this.track('Session Started', props);
  }

  trackLandingPageViewed(props: Omit<PageViewedProps, 'page'> & { page?: string } = {}): void {
    this.trackAnonymous('Landing Page Viewed', {
      page: props.page || window.location.pathname + window.location.hash,
      ...this.getAttributionProperties(),
      ...props,
    });
  }

  trackLoginScreenViewed(props: LoginScreenViewedProps): void {
    this.trackAnonymous('Login Screen Viewed', props);
  }

  trackAuthCtaClicked(props: Record<string, unknown>): void {
    this.trackAnonymous('Auth CTA Clicked', props);
  }

  trackLoginStarted(props: LoginStartedProps): void {
    this.trackAnonymous('Login Started', props);
  }

  trackLoginFailed(props: LoginFailedProps): void {
    this.trackAnonymous('Login Failed', props);
  }

  // ============================================================================
  // ACTIVATION Events
  // ============================================================================

  trackSignup(props: UserSignedUpProps): void {
    this.track('User Signed Up', props);
    this.setUserProperty('signup_date', new Date().toISOString());
    this.signupDate = new Date();
  }

  trackLogin(props: UserLoggedInProps): void {
    this.track('User Logged In', props);
    this.incrementUserProperty('login_count');
    this.setUserProperty('last_login', new Date().toISOString());
  }

  trackFirstResearchStarted(researchId: string, queryLength: number, model: string, timeToFirstResearchSeconds: number): void {
    this.track('First Research Started', {
      research_id: researchId,
      query_length: queryLength,
      model,
      time_to_first_research_seconds: timeToFirstResearchSeconds,
    });
    this.setUserProperty('first_research_date', new Date().toISOString());
  }

  trackFirstResearchCompleted(researchId: string, durationMinutes: number): void {
    this.track('First Research Completed', {
      research_id: researchId,
      duration_minutes: durationMinutes,
    });
  }

  trackPlanGenerated(props: Record<string, unknown>): void {
    this.track('Plan Generated', props);
  }

  trackPlanApproved(props: Record<string, unknown>): void {
    this.track('Plan Approved', props);
  }

  // ============================================================================
  // ENGAGEMENT Events
  // ============================================================================

  trackResearchStarted(props: ResearchStartedProps): void {
    this.userResearchCount++;
    const enrichedProps = {
      ...props,
      nth_research: this.userResearchCount,
      is_first_research: this.userResearchCount === 1,
    };
    
    this.track('Research Started', enrichedProps);
    this.incrementUserProperty('total_research');
    this.setUserProperty('last_research_date', new Date().toISOString());

    // Track first research separately for activation analysis
    if (this.userResearchCount === 1 && this.signupDate) {
      const timeToFirstResearch = Math.floor((Date.now() - this.signupDate.getTime()) / 1000);
      this.trackFirstResearchStarted(props.report_id || props.research_id, props.query_length, props.model, timeToFirstResearch);
    }
  }

  trackResearchCompleted(props: ResearchCompletedProps): void {
    this.track('Research Completed', props);
    this.incrementUserProperty('completed_research');
    if (this.userResearchCount === 1) {
      this.trackFirstResearchCompleted(props.report_id || props.research_id, props.duration_minutes);
    }
  }

  trackResearchFailed(props: ResearchFailedProps): void {
    this.track('Research Failed', props);
  }

  trackResearchRetried(props: Record<string, unknown>): void {
    this.track('Research Retried', props);
  }

  trackResearchViewed(props: ResearchViewedProps): void {
    this.track('Research Viewed', props);
  }

  trackFullReportRead(props: FullReportReadProps): void {
    this.track('Full Report Read', props);
  }

  trackArticleSaved(props: ArticleSavedProps): void {
    this.track('Article Saved', props);
    this.incrementUserProperty('saved_count');
  }

  trackArticleUnsaved(researchId: string, reportId?: string): void {
    this.track('Article Unsaved', {
      research_id: researchId,
      ...(reportId ? { report_id: reportId } : {}),
    });
    this.incrementUserProperty('saved_count', -1);
  }

  trackArticleLiked(props: ArticleLikedProps): void {
    this.track('Article Liked', props);
  }

  trackArticleUnliked(props: ArticleUnlikedProps): void {
    this.track('Article Unliked', props);
  }

  trackArticleShared(props: ArticleSharedProps): void {
    this.track('Article Shared', props);
    this.incrementUserProperty('shared_count');
  }

  trackExploreBrowsed(props: ExploreBrowsedProps): void {
    this.track('Explore Browsed', props);
  }

  trackExploreCardClicked(props: ExploreCardClickedProps): void {
    this.track('Explore Card Clicked', props);
  }

  trackCategorySelected(category: string, previousCategory?: string): void {
    this.track('Category Selected', { category, previous_category: previousCategory });
  }

  trackPodcastPlayed(props: PodcastPlayedProps): void {
    this.track('Podcast Played', props);
  }

  trackSourceClicked(props: SourceClickedProps): void {
    this.track('Source Clicked', props);
  }

  // ============================================================================
  // RETENTION Events
  // ============================================================================

  trackAppOpened(daysSinceLastOpen: number, sessionCount: number, isPushDriven: boolean = false, isColdStart: boolean = false): void {
    const sessionProps = {
      days_since_last_open: daysSinceLastOpen,
      session_count: sessionCount,
      is_push_driven: isPushDriven,
      is_cold_start: isColdStart,
    };
    this.track('App Opened', sessionProps);
    this.incrementUserProperty('days_active');
  }

  trackPushNotificationOpened(props: PushNotificationOpenedProps): void {
    this.track('Push Notification Opened', props);
  }

  trackPlanAbandoned(props: PlanAbandonedProps): void {
    this.track('Plan Abandoned', props);
  }

  // ============================================================================
  // MONETIZATION Events
  // ============================================================================

  trackLimitReached(props: LimitReachedProps): void {
    this.track('Limit Reached', props);
  }

  trackUpgradeModalViewed(props: UpgradeModalViewedProps): void {
    this.track('Upgrade Modal Viewed', props);
  }

  trackUpgradeStarted(props: UpgradeStartedProps): void {
    this.track('Upgrade Started', props);
  }

  trackUpgradeCompleted(props: UpgradeCompletedProps): void {
    this.track('Upgrade Completed', props);
    if (props.plan !== 'single') {
      this.setUserProperty('plan', props.plan);
    }
    
    // Track revenue
    const revenue = new amplitude.Revenue()
      .setProductId(props.plan)
      .setPrice(props.price_cents / 100)
      .setQuantity(1);
    amplitude.revenue(revenue);
  }

  // ============================================================================
  // NAVIGATION Events
  // ============================================================================

  trackTabChanged(props: TabChangedProps): void {
    this.track('Tab Changed', props);
  }

  trackModalOpened(props: ModalOpenedProps): void {
    this.track('Modal Opened', props);
  }

  trackModalClosed(props: ModalClosedProps): void {
    this.track('Modal Closed', props);
  }

  // ============================================================================
  // PROFILE Events
  // ============================================================================

  trackProfileViewed(props: ProfileViewedProps): void {
    this.track('Profile Viewed', props);
  }

  trackProfileResearchClicked(researchId: string, profileUserId: string, position: number): void {
    this.track('Profile Research Clicked', {
      research_id: researchId,
      profile_user_id: profileUserId,
      position,
    });
  }

  // ============================================================================
  // ERROR Events
  // ============================================================================

  trackError(props: ErrorOccurredProps): void {
    this.track('Error Occurred', props);
  }

  trackAPIError(props: APIErrorProps): void {
    this.track('API Error', props);
  }

  trackSlowLoad(page: string, loadTimeMs: number): void {
    this.track('Slow Load Detected', { page, load_time_ms: loadTimeMs });
  }

  // ============================================================================
  // Privacy Toggle Event
  // ============================================================================

  trackResearchPrivacyToggled(props: ResearchPrivacyToggledProps): void {
    this.track('Research Privacy Toggled', props);
  }

  // ============================================================================
  // GRANULAR SCREEN ENGAGEMENT TRACKING
  // ============================================================================

  /**
   * Track time spent on any screen with interaction details
   */
  trackScreenTime(screenName: string, timeSpentSeconds: number, interactionsCount: number, scrollDepthPercent?: number): void {
    this.track('Screen Time Tracked', {
      screen_name: screenName,
      time_spent_seconds: timeSpentSeconds,
      interactions_count: interactionsCount,
      scroll_depth_percent: scrollDepthPercent,
    });
    
    // Also update user property for total time
    this.incrementUserProperty(`total_time_${screenName}_seconds`, timeSpentSeconds);
  }

  /**
   * Track Research page engagement (My Research tab)
   */
  trackResearchPageEngagement(props: {
    time_spent_seconds: number;
    research_cards_viewed: number;
    research_cards_clicked: number;
    filter_changes: number;
    new_research_modal_opened: boolean;
    scroll_depth_percent: number;
  }): void {
    this.track('Research Page Engaged', props);
  }

  /**
   * Track Explore page engagement
   */
  trackExplorePageEngagement(props: {
    time_spent_seconds: number;
    cards_viewed: number;
    cards_clicked: number;
    categories_browsed: string[];
    scroll_depth_percent: number;
    search_queries?: number;
  }): void {
    this.track('Explore Page Engaged', props);
  }

  /**
   * Track Saved page engagement
   */
  trackSavedPageEngagement(props: {
    time_spent_seconds: number;
    saved_items_count: number;
    items_clicked: number;
    items_unsaved: number;
    scroll_depth_percent: number;
  }): void {
    this.track('Saved Page Engaged', props);
  }

  /**
   * Track Profile page engagement
   */
  trackProfilePageEngagement(props: {
    time_spent_seconds: number;
    is_own_profile: boolean;
    research_items_viewed: number;
    research_items_clicked: number;
    settings_opened: boolean;
    scroll_depth_percent: number;
  }): void {
    this.track('Profile Page Engaged', props);
  }

  /**
   * Track Article/Research detail page engagement
   */
  trackArticlePageEngagement(props: {
    research_id: string;
    time_spent_seconds: number;
    scroll_depth_percent: number;
    tabs_viewed: string[];
    sources_clicked: number;
    podcast_played: boolean;
    shared: boolean;
    saved: boolean;
    full_report_expanded: boolean;
    time_to_first_scroll_seconds?: number;
  }): void {
    this.track('Article Page Engaged', props);
    
    // Update user reading stats
    this.incrementUserProperty('total_reading_time_seconds', props.time_spent_seconds);
    this.incrementUserProperty('articles_read');
  }

  /**
   * Track scroll depth milestones (25%, 50%, 75%, 100%)
   */
  trackScrollDepth(screenName: string, depthPercent: number, timeToReachSeconds: number): void {
    // Only track significant milestones
    if (depthPercent === 25 || depthPercent === 50 || depthPercent === 75 || depthPercent === 100) {
      this.track('Scroll Depth Reached', {
        screen_name: screenName,
        depth_percent: depthPercent,
        time_to_reach_seconds: timeToReachSeconds,
      });
    }
  }

  /**
   * Track any content interaction
   */
  trackContentInteraction(screenName: string, interactionType: string, elementType: string, elementId?: string): void {
    this.track('Content Interaction', {
      screen_name: screenName,
      interaction_type: interactionType,
      element_type: elementType,
      element_id: elementId,
    });
  }
}

// Export singleton
export const analytics = new AmplitudeAnalytics();
export default analytics;

